import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useHistory } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ServiceAddOn } from '../../types/Service';
import { ServicesType } from '../../types/ServiceInstance';
import { closeIcon as CloseIcon } from './DiningMenuIcons';
import { MAX_ORDER_QUANTITY } from './diningMenuUtils';
import sendToast from '../../../utils/Toast';
import { MealItem } from '../../contexts/cart/CartContext';
import useActiveResident from '../../hooks/useActiveResident';
import useNeighborsPaymentAccess from '../../hooks/useNeighborsPaymentAccess';
import useResidentOrderCardEligibility from '../../hooks/useResidentOrderCardEligibility';
import { previewRequestInstanceCharge } from '../../services/Payments';
import { isPaymentRequiredForService } from '../../utils/paymentStatus';
import { getCardGateActionLabel, getCardGateMessage, resolveCardGateReason } from '../../utils/orderCardGate';

export type FulfillmentMethod = 'delivery' | 'pickUp' | 'dineIn';

export type PaymentConsent = NonNullable<MealItem['paymentConsent']>;

// Charge amounts the resident agreed to, keyed by cart entry.
export type PaymentConsentsByCartId = Record<string, PaymentConsent>;

type PendingCharge = {
    entriesSignature: string;
    totalCents: number;
    consents: PaymentConsentsByCartId;
    orderType: FulfillmentMethod;
    specialNotes: string;
    detail: string;
};

// What the price check depends on: items, quantities and add-ons per cart line.
const buildEntriesSignature = (entries: PlaceOrderEntry[]): string => JSON.stringify(
    entries.map((entry) => [
        entry.cartId,
        String(entry.itemId),
        entry.quantity,
        entry.addons.map((addon) => String(addon.id)).sort(),
    ]),
);

const CART_CHANGED_MESSAGE = 'Your order changed while we checked the price. Please place the order again.';

export type PlaceOrderEntry = {
    cartId: string;
    itemId: string;
    name: string;
    addons: ServiceAddOn[];
    item: ServicesType;
    restaurantName?: string;
    quantity: number;
};

interface Props {
    open: boolean;
    entries: PlaceOrderEntry[];
    placing: boolean;
    onClose: () => void;
    onRemove: (cartId: string) => void;
    onChangeQuantity: (cartId: string, quantity: number) => void;
    onToggleAddon: (cartId: string, addon: ServiceAddOn) => void;
    onPlaceOrder: (payload: {
        orderType: FulfillmentMethod;
        specialNotes: string;
        paymentConsents?: PaymentConsentsByCartId;
    }) => Promise<boolean>;
}

const FULFILLMENT_OPTIONS: { method: FulfillmentMethod; label: string; icon: React.ReactNode }[] = [
    {
        method: 'delivery',
        label: 'Delivery',
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1" y="3" width="15" height="13" rx="1" />
                <path d="M16 8h4l3 3v5h-7V8z" />
                <circle cx="5.5" cy="18.5" r="2.5" />
                <circle cx="18.5" cy="18.5" r="2.5" />
            </svg>
        ),
    },
    {
        method: 'pickUp',
        label: 'Pickup',
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 2l1.5 4.5h9L18 2" />
                <path d="M3 7h18l-1.5 12a2 2 0 01-2 1.8H6.5a2 2 0 01-2-1.8L3 7z" />
                <line x1="9" y1="11" x2="9" y2="16" />
                <line x1="15" y1="11" x2="15" y2="16" />
            </svg>
        ),
    },
    {
        method: 'dineIn',
        label: 'Dine In',
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 2v7c0 1.1.9 2 2 2h2a2 2 0 002-2V2" />
                <line x1="5" y1="11" x2="5" y2="22" />
                <path d="M17 2v20" />
                <path d="M13 8c0-3.3 1.8-6 4-6s4 2.7 4 6-1.8 6-4 6" />
            </svg>
        ),
    },
];

const METHOD_LABEL: Record<FulfillmentMethod, string> = {
    delivery: 'Delivery',
    pickUp: 'Pickup',
    dineIn: 'Dine In',
};

const uniqueAddons = (addons: ServiceAddOn[] = []) => {
    const seen = new Set<string>();
    return addons.filter((addon) => {
        if (!addon?.id || seen.has(addon.id)) return false;
        seen.add(addon.id);
        return true;
    });
};

const addonCost = (addon: ServiceAddOn) => Number(addon.itemCost) || 0;

const itemUnitCost = (item: ServicesType) =>
    item.defaults?.externalCost ? Number(item.defaults.externalCost) || 0 : 0;

const formatMoney = (value: number) => `$${value.toFixed(2)}`;

const formatPickupDateTime = (date: Date) =>
    date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });

const getLineTotal = (entry: PlaceOrderEntry) => {
    const addonsTotal = entry.addons.reduce((sum, addon) => sum + addonCost(addon), 0);
    return (itemUnitCost(entry.item) + addonsTotal) * entry.quantity;
};

const PlaceOrderPage = ({ open, entries, placing, onClose, onRemove, onChangeQuantity, onToggleAddon, onPlaceOrder }: Props) => {
    const [fulfillment, setFulfillment] = useState<FulfillmentMethod | null>(null);
    const [pickupTime, setPickupTime] = useState<Date | null>(null);
    const [specialNotes, setSpecialNotes] = useState('');
    const [confirmDetail, setConfirmDetail] = useState<string | null>(null);
    const [previewing, setPreviewing] = useState(false);
    const [pendingCharge, setPendingCharge] = useState<PendingCharge | null>(null);
    // Blocks a second Place Order / Continue before `placing` or `previewing` re-renders.
    const isSubmittingRef = useRef(false);
    // Latest cart, read after the price check resolves to catch edits made while it ran.
    const entriesSignatureRef = useRef('');
    entriesSignatureRef.current = buildEntriesSignature(entries);
    const history = useHistory();
    const activeResident = useActiveResident();
    const residentId = activeResident?._id ? String(activeResident._id) : '';
    const facilityId = activeResident?.Facility ? String(activeResident.Facility) : '';
    const {
        resolvedFacilityPaymentsEnabled,
        facilityLookupFailed,
        retry: retryFacilityPayments,
    } = useNeighborsPaymentAccess();
    const {
        loading: cardEligibilityLoading,
        hasDefaultCard,
        hasAnyCard,
        defaultCardExpired,
        lookupFailed,
        refresh: refreshCardEligibility,
    } = useResidentOrderCardEligibility({
        residentId,
        facilityId,
        enabled: open && resolvedFacilityPaymentsEnabled,
    });

    // Entries carry the loaded menu docs, so whether an item is payable is always known here.
    const orderContainsPayableItems = resolvedFacilityPaymentsEnabled
        && entries.some((entry) => isPaymentRequiredForService(entry.item));
    const cardGateReason = orderContainsPayableItems
        ? resolveCardGateReason({
            loading: cardEligibilityLoading,
            lookupFailed: lookupFailed || facilityLookupFailed,
            hasDefaultCard,
            hasAnyCard,
            defaultCardExpired,
        })
        : null;
    const cardGateHeading = cardGateReason === 'no_card' || cardGateReason === 'no_default' || cardGateReason === 'default_expired'
        ? 'Payment Method Required'
        : cardGateReason === 'lookup_failed'
            ? 'Could Not Verify Your Saved Card'
            : 'Checking Your Saved Card';

    useEffect(() => {
        if (!open) return undefined;

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open, onClose]);

    useEffect(() => {
        if (!open) {
            setFulfillment(null);
            setPickupTime(null);
            setSpecialNotes('');
            setConfirmDetail(null);
            setPreviewing(false);
            setPendingCharge(null);
        }
    }, [open]);

    // The agreed amount only covers the order as it was previewed.
    useEffect(() => {
        setPendingCharge(null);
    }, [entries]);

    const canSubmit = useMemo(() => {
        if (entries.length === 0 || !fulfillment || placing || previewing || cardGateReason) return false;
        if (fulfillment === 'pickUp') return pickupTime != null;
        return true;
    }, [entries.length, fulfillment, pickupTime, placing, previewing, cardGateReason]);

    const orderTotal = useMemo(
        () => entries.reduce((sum, entry) => sum + getLineTotal(entry), 0),
        [entries],
    );

    if (!open) return null;

    const submitOrder = async (
        orderType: FulfillmentMethod,
        notes: string,
        detail: string,
        paymentConsents?: PaymentConsentsByCartId,
    ) => {
        const ok = await onPlaceOrder({ orderType, specialNotes: notes, paymentConsents });
        if (ok) {
            setConfirmDetail(`${detail} — your order has been sent to the kitchen.`);
        }
        return ok;
    };

    // Asks the server for each line's tax-inclusive charge, as the order will be billed.
    const previewOrderCharge = async () => {
        const consentedAt = Date.now();
        const previews = await Promise.all(entries.map(async (entry) => ({
            entry,
            preview: await previewRequestInstanceCharge({
                facilityId,
                residentId,
                originalServiceInstanceId: String(entry.itemId),
                serviceAddOns: entry.addons.map((addon) => ({ id: String(addon.id) })),
                quantity: entry.quantity,
            }),
        })));

        let totalCents = 0;
        const consents: PaymentConsentsByCartId = {};
        previews.forEach(({ entry, preview }) => {
            if (!preview || !preview.requiresPayment) return;
            const amountCents = Number(preview.chargeBreakdown && preview.chargeBreakdown.totalCents);
            if (!Number.isFinite(amountCents) || amountCents < 0) {
                throw new Error('Unable to preview payment amount for one or more menu items');
            }
            consents[entry.cartId] = {
                amountDisplayedCents: Math.round(amountCents),
                confirmedAt: consentedAt,
                source: 'neighbors_dining',
            };
            totalCents += Math.round(amountCents);
        });
        return { totalCents, consents };
    };

    const handlePlace = async () => {
        if (!fulfillment || !canSubmit || isSubmittingRef.current) return;
        const userNotes = specialNotes.trim();
        const pickupLabel = pickupTime ? formatPickupDateTime(pickupTime) : '';
        const pickupLine =
            fulfillment === 'pickUp' && pickupLabel
                ? `Pickup time - ${pickupLabel}`
                : '';
        const notes = [userNotes, pickupLine].filter(Boolean).join('\n');
        let detail = METHOD_LABEL[fulfillment];
        if (fulfillment === 'pickUp' && pickupLabel) detail += ` at ${pickupLabel}`;

        isSubmittingRef.current = true;
        try {
            if (orderContainsPayableItems) {
                setPreviewing(true);
                const entriesSignature = entriesSignatureRef.current;
                const { totalCents, consents } = await previewOrderCharge();
                if (entriesSignatureRef.current !== entriesSignature) {
                    sendToast('warn', CART_CHANGED_MESSAGE);
                    return;
                }
                if (totalCents > 0) {
                    setPendingCharge({ entriesSignature, totalCents, consents, orderType: fulfillment, specialNotes: notes, detail });
                    return;
                }
            }
            await submitOrder(fulfillment, notes, detail);
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Unable to check the price of this order');
        } finally {
            setPreviewing(false);
            isSubmittingRef.current = false;
        }
    };

    const handleConfirmPayment = async () => {
        if (!pendingCharge || isSubmittingRef.current) return;
        if (entriesSignatureRef.current !== pendingCharge.entriesSignature) {
            setPendingCharge(null);
            sendToast('warn', CART_CHANGED_MESSAGE);
            return;
        }
        isSubmittingRef.current = true;
        try {
            // The agreed amount is recorded at the moment the resident presses Continue.
            const confirmedAt = Date.now();
            const consents = Object.keys(pendingCharge.consents).reduce<PaymentConsentsByCartId>((acc, cartId) => {
                acc[cartId] = { ...pendingCharge.consents[cartId], confirmedAt };
                return acc;
            }, {});
            // On failure the dialog keeps the amount so Continue retries it.
            await submitOrder(pendingCharge.orderType, pendingCharge.specialNotes, pendingCharge.detail, consents);
        } finally {
            isSubmittingRef.current = false;
        }
    };

    const handleRetryCardEligibility = async () => {
        if (facilityLookupFailed) retryFacilityPayments();
        await refreshCardEligibility();
    };

    const handleRemove = (cartId: string) => {
        onRemove(cartId);
    };

    return createPortal(
        <div
            className="review-overlay"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="review-modal" role="dialog" aria-modal="true" aria-labelledby="review-title">
                <div className="review-header">
                    <div className="review-title" id="review-title">
                        Your Order
                    </div>
                    <button type="button" className="review-close" onClick={onClose} aria-label="Close">
                        <CloseIcon />
                    </button>
                </div>

                {confirmDetail ? (
                    <div className="review-confirm show">
                        <div className="review-confirm-icon">
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </div>
                        <div className="review-confirm-title">Order Placed!</div>
                        <div className="review-confirm-sub">{confirmDetail}</div>
                        <button type="button" className="review-confirm-btn" onClick={onClose}>
                            Continue Browsing
                        </button>
                    </div>
                ) : pendingCharge ? (
                    <div className="review-body review-payment-confirm">
                        <div className="review-section-label">Confirm Payment</div>
                        <p className="review-payment-confirm-text">
                            This order requires payment. The estimated charge is{' '}
                            <strong>{formatMoney(pendingCharge.totalCents / 100)}</strong> including tax, charged to
                            your default card when the order is completed. Continue?
                        </p>
                        <div className="review-payment-confirm-actions">
                            <button
                                type="button"
                                className="review-confirm-btn"
                                disabled={placing}
                                onClick={() => setPendingCharge(null)}
                            >
                                Back
                            </button>
                            <button
                                type="button"
                                className="review-place-btn"
                                disabled={placing}
                                onClick={handleConfirmPayment}
                            >
                                {placing ? 'Placing…' : 'Continue'}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="review-body">
                        <div className="review-items">
                            {entries.length === 0 ? (
                                <div className="review-empty">Your order is empty.</div>
                            ) : (
                                entries.map((entry) => {
                                    const availableAddons = uniqueAddons(
                                        entry.item.ServiceAddOns?.length ? entry.item.ServiceAddOns : entry.addons,
                                    );
                                    const selectedIds = new Set(entry.addons.map((addon) => addon.id));
                                    const unitCost = itemUnitCost(entry.item);

                                    return (
                                    <div key={entry.cartId} className="review-item">
                                        <div className="review-item-top">
                                            <div className="review-item-name">{entry.name}</div>
                                            <div className="review-item-actions">
                                                <div className="qty-stepper review-item-qty">
                                                    <button
                                                        type="button"
                                                        aria-label={`Decrease ${entry.name}`}
                                                        onClick={() => onChangeQuantity(entry.cartId, entry.quantity - 1)}
                                                    >
                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                            <line x1="5" y1="12" x2="19" y2="12" />
                                                        </svg>
                                                    </button>
                                                    <span className="qty-stepper-value">{entry.quantity}</span>
                                                    <button
                                                        type="button"
                                                        aria-label={`Increase ${entry.name}`}
                                                        disabled={entry.quantity >= MAX_ORDER_QUANTITY}
                                                        onClick={() => onChangeQuantity(
                                                            entry.cartId,
                                                            Math.min(MAX_ORDER_QUANTITY, entry.quantity + 1),
                                                        )}
                                                    >
                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                            <line x1="12" y1="5" x2="12" y2="19" />
                                                            <line x1="5" y1="12" x2="19" y2="12" />
                                                        </svg>
                                                    </button>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="review-item-remove"
                                                    aria-label={`Remove ${entry.name}`}
                                                    onClick={() => handleRemove(entry.cartId)}
                                                >
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <line x1="18" y1="6" x2="6" y2="18" />
                                                        <line x1="6" y1="6" x2="18" y2="18" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                        {unitCost > 0 ? (
                                            <div className="review-item-price">{formatMoney(unitCost)}</div>
                                        ) : null}
                                        {availableAddons.length > 0 ? (
                                            <>
                                                <div className="review-item-addons-label">Add-Ons</div>
                                                <div className="review-item-addons-chips">
                                                    {availableAddons.map((addon) => {
                                                        const selected = selectedIds.has(addon.id) || addon.required;
                                                        const price = addonCost(addon);
                                                        return (
                                                            <button
                                                                key={addon.id}
                                                                type="button"
                                                                className={`review-item-addon-chip${selected ? ' selected' : ''}${addon.required ? ' required' : ''}`}
                                                                disabled={addon.required}
                                                                aria-pressed={selected}
                                                                onClick={() => onToggleAddon(entry.cartId, addon)}
                                                            >
                                                                {selected ? (
                                                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                                        <polyline points="20 6 9 17 4 12" />
                                                                    </svg>
                                                                ) : null}
                                                                <span>{addon.itemName}</span>
                                                                {price > 0 ? (
                                                                    <span className="review-item-addon-price">{formatMoney(price)}</span>
                                                                ) : null}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </>
                                        ) : null}
                                    </div>
                                    );
                                })
                            )}
                        </div>

                        <div className="review-section-label">How Would You Like It?</div>
                        <div className="review-fulfillment-options">
                            {FULFILLMENT_OPTIONS.map((option) => (
                                <button
                                    key={option.method}
                                    type="button"
                                    className={`review-fulfillment-option${fulfillment === option.method ? ' selected' : ''}`}
                                    onClick={() => {
                                        setFulfillment(option.method);
                                        if (option.method !== 'pickUp') setPickupTime(null);
                                    }}
                                >
                                    <span className="icon-circle">{option.icon}</span>
                                    <span className="label">{option.label}</span>
                                </button>
                            ))}
                        </div>

                        {fulfillment === 'pickUp' ? (
                            <div className="review-time-field">
                                <label htmlFor="review-pickup-time">Pickup Time</label>
                                <DatePicker
                                    id="review-pickup-time"
                                    selected={pickupTime}
                                    onChange={(date: Date | null) => setPickupTime(date)}
                                    showTimeSelect
                                    timeIntervals={15}
                                    dateFormat="MMM d, yyyy h:mm aa"
                                    placeholderText="e.g. 12:30 PM"
                                    minDate={new Date()}
                                    filterTime={(time) => {
                                        const now = new Date();
                                        const day = pickupTime || time;
                                        if (day.toDateString() !== now.toDateString()) return true;
                                        return time.getTime() >= now.getTime();
                                    }}
                                    autoComplete="off"
                                    popperClassName="review-datepicker-popper"
                                    popperProps={{ strategy: 'fixed' }}
                                />
                            </div>
                        ) : null}

                        {entries.length > 0 ? (
                            <div className="review-total">
                                <span>{orderContainsPayableItems ? 'Subtotal (before tax)' : 'Total'}</span>
                                <span>{formatMoney(orderTotal)}</span>
                            </div>
                        ) : null}

                        {cardGateReason ? (
                            <div className="review-card-gate" role="status">
                                <div className="review-card-gate-title">{cardGateHeading}</div>
                                <p>{getCardGateMessage(cardGateReason)}</p>
                                <div className="review-card-gate-actions">
                                    {cardGateReason !== 'checking' ? (
                                        <button type="button" className="review-card-gate-primary" onClick={() => history.push('/payments')}>
                                            {getCardGateActionLabel(cardGateReason)}
                                        </button>
                                    ) : null}
                                    <button
                                        type="button"
                                        className="review-confirm-btn"
                                        disabled={cardEligibilityLoading}
                                        onClick={handleRetryCardEligibility}
                                    >
                                        Retry
                                    </button>
                                </div>
                            </div>
                        ) : null}

                        <div className="review-time-field">
                            <textarea
                                id="review-special-notes"
                                placeholder="Special Notes:"
                                value={specialNotes}
                                rows={3}
                                onChange={(e) => setSpecialNotes(e.target.value)}
                            />
                        </div>

                        <button
                            type="button"
                            className="review-place-btn"
                            disabled={!canSubmit}
                            onClick={handlePlace}
                        >
                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M9 11l3 3L22 4" />
                                <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
                            </svg>
                            {placing ? 'Placing…' : previewing ? 'Checking price…' : 'Place Order'}
                        </button>
                    </div>
                )}
            </div>
        </div>,
        document.body,
    );
};

export default PlaceOrderPage;
